<script>
import { ref } from 'vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import FileUpload from 'vue-upload-component';
import * as ActiveStorage from 'activestorage';
import inboxMixin from 'shared/mixins/inboxMixin';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { getAllowedFileTypesByChannel } from '@chatwoot/utils';
import { ALLOWED_FILE_TYPES } from 'shared/constants/messages';
import VideoCallButton from '../VideoCallButton.vue';
import LineStickerPicker from '../LineStickerPicker.vue';
import LineEmojiPicker from '../LineEmojiPicker.vue';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import { mapGetters } from 'vuex';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  name: 'ReplyBottomPanel',
  components: {
    NextButton,
    FileUpload,
    VideoCallButton,
    LineStickerPicker,
    LineEmojiPicker,
  },
  mixins: [inboxMixin],
  props: {
    isNote: {
      type: Boolean,
      default: false,
    },
    onSend: {
      type: Function,
      default: () => {},
    },
    sendButtonText: {
      type: String,
      default: '',
    },
    recordingAudioDurationText: {
      type: String,
      default: '00:00',
    },
    // inbox prop is used in /mixins/inboxMixin,
    // remove this props when refactoring to composable if not needed
    // eslint-disable-next-line vue/no-unused-properties
    inbox: {
      type: Object,
      default: () => ({}),
    },
    showFileUpload: {
      type: Boolean,
      default: false,
    },
    showAudioRecorder: {
      type: Boolean,
      default: false,
    },
    onFileUpload: {
      type: Function,
      default: () => {},
    },
    toggleEmojiPicker: {
      type: Function,
      default: () => {},
    },
    toggleAudioRecorder: {
      type: Function,
      default: () => {},
    },
    toggleAudioRecorderPlayPause: {
      type: Function,
      default: () => {},
    },
    isRecordingAudio: {
      type: Boolean,
      default: false,
    },
    recordingAudioState: {
      type: String,
      default: '',
    },
    isSendDisabled: {
      type: Boolean,
      default: false,
    },
    showEditorToggle: {
      type: Boolean,
      default: false,
    },
    isOnPrivateNote: {
      type: Boolean,
      default: false,
    },
    enableMultipleFileUpload: {
      type: Boolean,
      default: true,
    },
    enableWhatsAppTemplates: {
      type: Boolean,
      default: false,
    },
    enableContentTemplates: {
      type: Boolean,
      default: false,
    },
    conversationId: {
      type: [String, Number],
      default: null,
    },
    lineUserId: {
      type: String,
      default: '',
    },
    message: {
      type: String,
      default: '',
    },
    newConversationModalActive: {
      type: Boolean,
      default: false,
    },
    portalSlug: {
      type: String,
      required: true,
    },
    conversationType: {
      type: String,
      default: '',
    },
    lockToPrivateNote: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'replaceText',
    'toggleInsertArticle',
    'toggleEditor',
    'selectWhatsappTemplate',
    'selectContentTemplate',
    'toggleMode',
    'toggleCannedPicker',
    'sendLineEmoji',
  ],
  setup() {
    const { setSignatureFlagForInbox, fetchSignatureFlagFromUISettings } =
      useUISettings();

    const uploadRef = ref(false);

    const keyboardEvents = {
      '$mod+Alt+KeyA': {
        action: () => {
          // TODO: This is really hacky, we need to replace the file picker component with
          // a custom one, where the logic and the component markup is isolated.
          // Once we have the custom component, we can remove the hacky logic below.

          const uploadTriggerButton = document.querySelector(
            '#conversationAttachment'
          );
          uploadTriggerButton.click();
        },
        allowOnFocusedInput: true,
      },
    };

    useKeyboardEvents(keyboardEvents);

    return {
      setSignatureFlagForInbox,
      fetchSignatureFlagFromUISettings,
      uploadRef,
    };
  },
  data() {
    return {
      ALLOWED_FILE_TYPES,
      showLineStickerPicker: false,
      showLineEmojiPicker: false,
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      uiFlags: 'integrations/getUIFlags',
    }),
    wrapClass() {
      return {
        'is-note-mode': this.isNote,
      };
    },
    showAttachButton() {
      return this.showFileUpload || this.isNote;
    },
    showAudioRecorderButton() {
      if (this.isALineChannel) {
        return false;
      }
      // Disable audio recorder for safari browser as recording is not supported
      // const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(
      //   navigator.userAgent
      // );

      return (
        this.isFeatureEnabledonAccount(
          this.accountId,
          FEATURE_FLAGS.VOICE_RECORDER
        ) && this.showAudioRecorder
        // !isSafari
      );
    },
    showAudioPlayStopButton() {
      return this.showAudioRecorder && this.isRecordingAudio;
    },
    isInstagramDM() {
      return this.conversationType === 'instagram_direct_message';
    },
    allowedFileTypes() {
      // Use default file types for private notes
      if (this.isOnPrivateNote) {
        return this.ALLOWED_FILE_TYPES;
      }

      let channelType = this.channelType || this.inbox?.channel_type;

      if (this.isAnInstagramChannel || this.isInstagramDM) {
        channelType = INBOX_TYPES.INSTAGRAM;
      }

      return getAllowedFileTypesByChannel({
        channelType,
        medium: this.inbox?.medium,
      });
    },
    enableDragAndDrop() {
      return !this.newConversationModalActive;
    },
    audioRecorderPlayStopIcon() {
      switch (this.recordingAudioState) {
        // playing paused recording stopped inactive destroyed
        case 'playing':
          return 'i-ph-pause';
        case 'paused':
          return 'i-ph-play';
        case 'stopped':
          return 'i-ph-play';
        default:
          return 'i-ph-stop';
      }
    },
    showMessageSignatureButton() {
      return !this.isOnPrivateNote;
    },
    sendWithSignature() {
      // channelType is sourced from inboxMixin
      return this.fetchSignatureFlagFromUISettings(this.channelType);
    },
    signatureToggleTooltip() {
      return this.sendWithSignature
        ? this.$t('CONVERSATION.FOOTER.DISABLE_SIGN_TOOLTIP')
        : this.$t('CONVERSATION.FOOTER.ENABLE_SIGN_TOOLTIP');
    },
    enableInsertArticleInReply() {
      return this.portalSlug;
    },
    isFetchingAppIntegrations() {
      return this.uiFlags.isFetching;
    },
    isLineChannel() {
      return this.inbox?.channel_type === 'Channel::Line';
    },
  },
  mounted() {
    ActiveStorage.start();
  },
  methods: {
    toggleMessageSignature() {
      this.setSignatureFlagForInbox(this.channelType, !this.sendWithSignature);
    },
    replaceText(text) {
      this.$emit('replaceText', text);
    },
    toggleInsertArticle() {
      this.$emit('toggleInsertArticle');
    },
    openLineStickerPicker() {
      // 显示 LINE Sticker 选择器弹窗
      this.showLineStickerPicker = true;
    },

    closeLineStickerPicker() {
      this.showLineStickerPicker = false;
    },

    openLineEmojiPicker() {
      this.showLineEmojiPicker = true;
    },

    closeLineEmojiPicker() {
      this.showLineEmojiPicker = false;
    },

    onLineEmojiSent(emojiData) {
      this.$emit('sendLineEmoji', emojiData);
      this.showLineEmojiPicker = false;
    },

    onStickerSent(stickerData) {
      // 贴图发送成功后的处理
      console.log('Sticker sent:', stickerData);
      this.showLineStickerPicker = false;
      // 可以在这里添加成功提示或其他处理
    },

    onStickerInserted(stickerData) {
      // 贴图插入到输入框的处理
      console.log('Sticker inserted:', stickerData);
      this.showLineStickerPicker = false;

      // 触发事件将贴图文本插入到输入框
      this.$emit('insert-sticker', stickerData);
    },
  },
};
</script>

<template>
  <div class="wlab-reply-toolbar" :class="wrapClass">
    <div class="wlab-reply-toolbar__left">
      <!-- Mode toggle: Message / Note (replaces the top panel toggle) -->
      <button
        v-if="!lockToPrivateNote"
        class="wlab-reply-toolbar__mode-btn"
        :class="{ 'wlab-reply-toolbar__mode-btn--note': isNote }"
        @click="$emit('toggleMode')"
      >
        <span class="wlab-reply-toolbar__mode-icon">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            width="18"
            height="18"
          >
            <path
              v-if="!isNote"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="m3 20 1.3-3.9c-1.124-1.662-1.53-3.63-1.144-5.538S4.696 6.936 6.4 5.726s3.845-1.827 6.024-1.739c2.179.089 4.248.877 5.821 2.22 1.574 1.342 2.546 3.147 2.735 5.078s-.417 3.858-1.706 5.422c-1.29 1.564-3.173 2.659-5.302 3.08-2.13.422-4.358.142-6.272-.787z"
            />
            <path
              v-else
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 20h9M16.376 3.622a2.121 2.121 0 1 1 3 3L7.5 18.5 3 20l1.5-4.5z"
            />
          </svg>
        </span>
        <span class="wlab-reply-toolbar__mode-label">{{
          isNote ? 'Note' : 'Message'
        }}</span>
        <span class="wlab-reply-toolbar__mode-chevron">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            width="16"
            height="16"
          >
            <path
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="m6 9 6 6 6-6"
            />
          </svg>
        </span>
      </button>
      <div v-else class="wlab-reply-toolbar__mode-chip">
        <span class="wlab-reply-toolbar__mode-icon">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            width="18"
            height="18"
          >
            <path
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 20h9M16.376 3.622a2.121 2.121 0 1 1 3 3L7.5 18.5 3 20l1.5-4.5z"
            />
          </svg>
        </span>
        <span class="wlab-reply-toolbar__mode-label">
          {{ $t('CONVERSATION.REPLYBOX.INTERNAL_NOTE') }}
        </span>
      </div>

      <!-- Separator -->
      <div class="wlab-reply-toolbar__separator" />

      <!-- Canned responses (#) -->
      <NextButton
        v-tooltip.top-end="'Canned Responses ( / )'"
        icon="i-ph-hash-bold"
        slate
        faded
        md
        class="wlab-reply-toolbar__icon-btn"
        @click="$emit('toggleCannedPicker')"
      />

      <!-- Attachment -->
      <FileUpload
        ref="uploadRef"
        v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON')"
        input-id="conversationAttachment"
        :size="4096 * 4096"
        :accept="allowedFileTypes"
        :multiple="enableMultipleFileUpload"
        :drop="enableDragAndDrop"
        :drop-directory="false"
        :data="{
          direct_upload_url: '/rails/active_storage/direct_uploads',
          direct_upload: true,
        }"
        @input-file="onFileUpload"
      >
        <NextButton
          v-if="showAttachButton"
          v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON')"
          icon="i-ph-paperclip-bold"
          slate
          faded
          md
          class="wlab-reply-toolbar__icon-btn"
        />
      </FileUpload>

      <!-- Emoji -->
      <NextButton
        v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_EMOJI_ICON')"
        icon="i-ph-smiley-bold"
        slate
        faded
        md
        class="wlab-reply-toolbar__icon-btn"
        @click.stop="toggleEmojiPicker($event)"
      />

      <!-- Editor toggle (rich text) -->
      <NextButton
        v-if="showEditorToggle"
        v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_FORMAT_ICON')"
        icon="i-ph-text-aa-bold"
        slate
        faded
        md
        class="wlab-reply-toolbar__icon-btn"
        @click="$emit('toggleEditor')"
      />

      <!-- WhatsApp templates -->
      <NextButton
        v-if="enableWhatsAppTemplates"
        v-tooltip.top-end="$t('CONVERSATION.FOOTER.WHATSAPP_TEMPLATES')"
        icon="i-ph-whatsapp-logo"
        slate
        faded
        md
        class="wlab-reply-toolbar__icon-btn"
        @click="$emit('selectWhatsappTemplate')"
      />

      <!-- Content templates -->
      <NextButton
        v-if="enableContentTemplates"
        v-tooltip.top-end="'Content Templates'"
        icon="i-ph-whatsapp-logo"
        slate
        faded
        md
        class="wlab-reply-toolbar__icon-btn"
        @click="$emit('selectContentTemplate')"
      />

      <!-- Video Call -->
      <VideoCallButton
        v-if="(isAWebWidgetInbox || isAPIInbox) && !isOnPrivateNote"
        :conversation-id="conversationId"
      />

      <!-- LINE sticker -->
      <NextButton
        v-if="isLineChannel && !isOnPrivateNote"
        v-tooltip.top-end="'Send LINE Sticker'"
        icon="i-ph-smiley-sticker-duotone"
        slate
        faded
        md
        class="wlab-reply-toolbar__icon-btn"
        @click="openLineStickerPicker"
      />

      <!-- LINE emoji -->
      <NextButton
        v-if="isLineChannel && !isOnPrivateNote"
        v-tooltip.top-end="'Send LINE Emoji'"
        icon="i-ph-smiley"
        slate
        faded
        md
        class="wlab-reply-toolbar__icon-btn"
        @click="openLineEmojiPicker"
      />

      <!-- Insert article -->
      <NextButton
        v-if="enableInsertArticleInReply"
        v-tooltip.top-end="$t('HELP_CENTER.ARTICLE_SEARCH.OPEN_ARTICLE_SEARCH')"
        icon="i-ph-article-ny-times"
        slate
        faded
        md
        class="wlab-reply-toolbar__icon-btn"
        @click="toggleInsertArticle"
      />

      <!-- Audio recorder buttons (only when recording) -->
      <NextButton
        v-if="showAudioPlayStopButton"
        :icon="audioRecorderPlayStopIcon"
        slate
        faded
        md
        :label="recordingAudioDurationText"
        class="wlab-reply-toolbar__icon-btn"
        @click="toggleAudioRecorderPlayPause"
      />

      <!-- Drop zone overlay -->
      <transition name="modal-fade">
        <div
          v-show="uploadRef && uploadRef.dropActive"
          class="flex fixed top-0 right-0 bottom-0 left-0 z-20 flex-col gap-2 justify-center items-center w-full h-full text-n-slate-12 bg-modal-backdrop-light dark:bg-modal-backdrop-dark"
        >
          <fluent-icon icon="cloud-backup" size="40" />
          <h4 class="text-2xl break-words text-n-slate-12">
            {{ $t('CONVERSATION.REPLYBOX.DRAG_DROP') }}
          </h4>
        </div>
      </transition>
    </div>

    <!-- Send button -->
    <button
      class="wlab-reply-toolbar__send-btn"
      :class="{
        'wlab-reply-toolbar__send-btn--note': isNote,
        'wlab-reply-toolbar__send-btn--disabled': isSendDisabled,
      }"
      :disabled="isSendDisabled"
      type="submit"
      @click="onSend"
    >
      {{ sendButtonText }}
    </button>
  </div>

  <!-- LINE Sticker Picker Modal -->
  <LineStickerPicker
    :show="showLineStickerPicker"
    :conversation-id="conversationId"
    :inbox-id="inbox.id"
    :line-user-id="lineUserId"
    @close="closeLineStickerPicker"
    @sticker-sent="onStickerSent"
    @insert-sticker="onStickerInserted"
  />

  <LineEmojiPicker
    :show="showLineEmojiPicker"
    @close="closeLineEmojiPicker"
    @emoji-sent="onLineEmojiSent"
  />
</template>

<style lang="scss" scoped>
.wlab-reply-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px;
  padding-right: 8px;
}

.wlab-reply-toolbar__left {
  display: flex;
  align-items: center;
  gap: 2px;
  overflow: hidden;
  max-width: calc(100% - 82px);
  width: 100%;
}

.wlab-reply-toolbar__mode-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 6px 6px 12px;
  border: 1px solid transparent;
  border-radius: 8px;
  background: transparent;
  color: var(--wlab-text-primary, #131317);
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  white-space: nowrap;
  transition: background-color 0.15s ease;

  &:hover {
    background: var(--wlab-bg-hover, #f1f5f9);
  }

  &--note {
    color: #b45309;
  }
}

.wlab-reply-toolbar__mode-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 8px;
  background: rgba(217, 119, 6, 0.1);
  color: #b45309;
  font-size: 13px;
  font-weight: 600;
  line-height: 20px;
  white-space: nowrap;
}

.wlab-reply-toolbar__mode-icon {
  display: flex;
  align-items: center;
  width: 18px;
  height: 18px;
}

.wlab-reply-toolbar__mode-label {
  font-size: 13px;
  font-weight: 600;
}

.wlab-reply-toolbar__mode-chevron {
  display: flex;
  align-items: center;
  margin-left: 2px;
  opacity: 0.5;
}

.wlab-reply-toolbar__separator {
  width: 1px;
  height: 24px;
  background: var(--wlab-border-default, #e2e2e4);
  margin: 4px 8px 2px 4px;
  flex-shrink: 0;
}

.wlab-reply-toolbar__icon-btn {
  :deep(span[class*='i-ph-']) {
    width: 18px !important;
    height: 18px !important;
  }
}

.wlab-reply-toolbar__send-btn {
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 68px;
  height: 32px;
  padding: 6px 16px;
  border: none;
  border-radius: 8px;
  background-color: var(--wlab-text-primary, #131317);
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  white-space: nowrap;
  cursor: pointer;
  flex-shrink: 0;
  transition: opacity 0.15s ease;

  &:hover:not(:disabled) {
    opacity: 0.85;
  }

  &--note {
    background-color: #d97706;
    color: #ffffff;
  }

  &--disabled {
    background-color: rgba(27, 27, 32, 0.2);
    color: rgba(255, 255, 255, 0.6);
    cursor: not-allowed;
  }
}

::v-deep .file-uploads {
  label {
    cursor: pointer;
  }
}
</style>
